import { ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'wouter';
import type { User } from 'firebase/auth';
import { Search, Sun, Moon, Monitor } from 'lucide-react';
import dayjs from 'dayjs';
import BrandMark from './BrandMark';
import CommandPalette from './CommandPalette';
import UserMenu from './UserMenu';
import { navGroups } from './nav';
import { useThemePref, setPref, type ThemePref } from '../lib/theme';
import { useRole } from '../lib/useRole';
import { useSlidingPill } from '../lib/useSlidingPill';
import { useCreatorPendingCount } from '../lib/api/useCreatorProgram';
import { useAffiliatePendingCount } from '../lib/api/useAffiliates';

type Badges = Partial<Record<NonNullable<ReturnType<typeof navGroups>[number][number]['badge']>, number>>;

/** Amber count chip — the same one PillTabs draws for a page's own tabs. */
function NavBadge({ n }: { n: number | undefined }) {
  if (!n) return null;
  return (
    <span aria-label={`${n} pending`} className="ml-1.5 rounded-full bg-signal-warning px-1.5 py-px text-[10px] font-semibold leading-tight text-neutral-950">
      {n}
    </span>
  );
}

interface LayoutProps {
  user: User;
  children: ReactNode;
  /**
   * Overrides the signed-in role when deciding which tabs to show. Only the
   * dev preview harness (`preview.html`) passes this — the app itself always
   * takes the role off the Firebase ID token.
   */
  roleOverride?: ReturnType<typeof useRole>;
}

/** Brand rail (56px) + tab row (68px). Kept in sync with the main padding below. */
const CHROME_HEIGHT = 124;

function useScrolled(threshold = 8): boolean {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);
  return scrolled;
}

function useCommandKey(open: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        open();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
}

const THEME_CYCLE: ThemePref[] = ['system', 'light', 'dark'];
const THEME_ICON = { system: Monitor, light: Sun, dark: Moon } as const;

function ThemeToggle() {
  const pref = useThemePref();
  const Icon = THEME_ICON[pref];
  const next = THEME_CYCLE[(THEME_CYCLE.indexOf(pref) + 1) % THEME_CYCLE.length];
  return (
    <button
      onClick={() => setPref(next)}
      aria-label={`Theme: ${pref} — switch to ${next}`}
      title={`Theme: ${pref}`}
      className="flex h-9 w-9 items-center justify-center rounded-full border border-neutral-800 bg-neutral-900/60 text-neutral-500 transition-colors hover:border-neutral-700 hover:bg-neutral-800/70 hover:text-neutral-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-mint-300/70"
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

type NavTab = ReturnType<typeof navGroups>[number][number];

/**
 * One capsule of the tab row. The white lozenge is a single element that
 * slides under whichever tab matches the route (`useSlidingPill`); a capsule
 * whose tabs are all inactive hides its lozenge.
 */
function NavCapsule({
  group,
  alt,
  location,
  badges,
}: {
  group: NavTab[];
  alt: boolean;
  location: string;
  badges: Badges;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useSlidingPill(ref, location);
  return (
    <div ref={ref} className={`pill-tabs relative ${alt ? 'pill-tabs-alt' : ''}`}>
      {/* Divider tick between the capsules. */}
      {alt && (
        <span
          aria-hidden
          className="absolute -left-[15px] top-1/2 h-[22px] w-0.5 -translate-y-1/2 rounded-sm bg-gradient-to-b from-transparent via-white/20 to-transparent"
        />
      )}
      <span className="pill-tabs-pill" aria-hidden="true" />
      {group.map((tab) => {
        const isActive = location === tab.path;
        return (
          <Link
            key={tab.path}
            href={tab.path}
            aria-current={isActive ? 'page' : undefined}
            aria-selected={isActive}
            className="pill-tab focus:outline-none focus-visible:ring-2 focus-visible:ring-mint-300/70"
          >
            {tab.label}
            {tab.badge && <NavBadge n={badges[tab.badge]} />}
          </Link>
        );
      })}
    </div>
  );
}

export default function Layout({ user, children, roleOverride }: LayoutProps) {
  const [location] = useLocation();
  // Always call the hook — `??` short-circuits, and a conditional hook call
  // would break the rules of hooks the moment the override is passed.
  const liveRole = useRole();
  const role = roleOverride ?? liveRole;
  const scrolled = useScrolled();
  const [paletteOpen, setPaletteOpen] = useState(false);
  useCommandKey(() => setPaletteOpen(true));
  const navRef = useRef<HTMLElement>(null);

  const groups = navGroups(role);
  // The /creators badge: clips waiting for review. Only polled for roles
  // that can open the panel.
  const creatorsPending = useCreatorPendingCount(!role.loading && role.hasCreativeAccess);
  const affiliatesPending = useAffiliatePendingCount(!role.loading && role.hasCreativeAccess);
  const badges: Badges = { creatorsPending, affiliatesPending };

  // On narrow screens the tab strip scrolls — keep the current section visible.
  useEffect(() => {
    navRef.current
      ?.querySelector<HTMLElement>('[aria-current="page"]')
      ?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [location, groups.length]);

  return (
    <div className="app-canvas min-h-dvh">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[200] focus:rounded-lg focus:bg-mint-300 focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-neutral-950"
      >
        Skip to content
      </a>

      <header
        className={`fixed inset-x-0 top-0 z-40 border-b bg-neutral-950/85 backdrop-blur-xl transition-[border-color,box-shadow] duration-200 ${
          scrolled
            ? 'border-neutral-800 shadow-[var(--header-shadow)]'
            : 'border-neutral-900'
        }`}
      >
        {/* ── brand rail ─────────────────────────────────────────────────
            `Outclose / growth command center`, with the mint slash the
            reference page uses as its one piece of colour up here. */}
        <div className="mx-auto flex h-14 max-w-7xl items-center gap-3 px-4 sm:px-6">
          <Link
            href="/"
            aria-label="Outclose Growth — overview"
            className="group flex min-w-0 shrink items-center gap-2.5 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-mint-300/70"
          >
            <BrandMark className="h-7 w-7 shrink-0 transition-transform group-hover:scale-105" />
            <span className="truncate text-[17px] font-semibold tracking-[-0.03em] text-neutral-50">
              Outclose
              <span className="mx-1.5 font-normal text-mint-300">/</span>
              <span className="hidden font-normal text-neutral-300 sm:inline">
                growth command center
              </span>
            </span>
          </Link>

          <div className="ml-auto flex shrink-0 items-center gap-2">
            <span className="hidden items-center gap-2 pr-1 font-mono text-[11px] uppercase tracking-[0.12em] text-neutral-500 lg:flex">
              <span className="live-dot" />
              {dayjs().format('YYYY-MM-DD')} · Live
            </span>
            <ThemeToggle />
            <button
              onClick={() => setPaletteOpen(true)}
              aria-label="Jump to a section (Command K)"
              className="flex h-9 w-9 cursor-pointer items-center justify-center gap-2 rounded-full border border-neutral-800 bg-neutral-900/60 text-sm text-neutral-500 transition-colors hover:border-neutral-700 hover:bg-neutral-800/70 hover:text-neutral-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-mint-300/70 md:w-auto md:justify-start md:pl-3 md:pr-2"
            >
              <Search className="h-4 w-4 shrink-0" />
              <span className="hidden md:block">Jump to…</span>
              <kbd className="hidden rounded border border-neutral-700 px-1.5 py-0.5 font-sans text-[10px] font-medium text-neutral-500 md:block">
                ⌘K
              </kbd>
            </button>
            <UserMenu user={user} />
          </div>
        </div>

        {/* ── section tabs ───────────────────────────────────────────────
            Three capsules (acquisition, business, reference) in one strip that
            scrolls sideways, like the main app's admin nav. */}
        <nav
          ref={navRef}
          aria-label="Primary"
          className="mx-auto max-w-7xl overflow-x-auto overscroll-x-contain px-4 pb-3.5 pr-8 no-scrollbar scroll-smooth [mask-image:linear-gradient(to_right,black_0,black_calc(100%-2rem),transparent)] sm:px-6 sm:pr-10"
        >
          <div className="flex items-center gap-7">
            {groups.map((group, groupIndex) => (
              <NavCapsule
                key={group[0].path}
                group={group}
                alt={groupIndex > 0}
                location={location}
                badges={badges}
              />
            ))}
          </div>
        </nav>
      </header>

      <main
        id="main"
        className="relative z-[1] mx-auto max-w-7xl px-4 pb-20 sm:px-6"
        style={{ paddingTop: CHROME_HEIGHT + 28 }}
      >
        {/* Keyed on the route so each page enters with the same short rise. */}
        <div key={location} className="page-enter">
          {children}
        </div>
      </main>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
